import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, X } from "lucide-react";

// Cover-pass offer overlay — Mochi proactively offers her weekly pass when:
//   - it's after 8pm EST
//   - a habit with streak ≥ 3 is unfinished today
//   - the weekly pass is still available
//
// Renders as a small cream card directly under the speech bubble. The
// message + accept/decline copy are passed in by the parent (rotating
// banks live in lib/coverPass.js).
//
// Accept → triggers wink animation + chime + star indicator + toast.
// Decline → no consumption, no follow-up nudge.
export default function CoverPassOffer({
    open,
    message,
    habitTitle,
    onAccept,
    onDecline,
    onDismiss,
}) {
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.96 }}
                    transition={{ type: "spring", stiffness: 220, damping: 24 }}
                    className="mx-auto max-w-sm w-[92%] bg-cream rounded-clay border border-mustard/40 shadow-clay px-4 py-3.5 relative"
                    data-testid="cover-pass-offer"
                >
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={onDismiss}
                        className="absolute top-2 right-2 text-warm-500 hover:text-warm-900 p-1 rounded-full hover:bg-surface"
                        data-testid="cover-pass-dismiss"
                    >
                        <X size={14} />
                    </button>

                    <div className="flex items-center gap-2 mb-1">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-mustard-soft text-mustard">
                            <Star size={14} fill="currentColor" />
                        </span>
                        <p
                            className="font-display text-[10px] uppercase tracking-widest text-warm-500"
                            data-testid="cover-pass-label"
                        >
                            Mochi has a cover pass
                        </p>
                    </div>

                    <p
                        className="font-body text-sm text-warm-900 leading-snug pr-2"
                        data-testid="cover-pass-message"
                    >
                        {message}
                    </p>

                    <div className="mt-3 flex gap-2">
                        <button
                            onClick={onAccept}
                            className="flex-1 bg-mustard hover:opacity-90 text-warm-900 font-display text-sm py-2 rounded-full transition-all"
                            data-testid="cover-pass-accept"
                        >
                            yes, cover {habitTitle ? `"${habitTitle}"` : "it"} 🍡
                        </button>
                        <button
                            onClick={onDecline}
                            className="flex-1 bg-surface hover:bg-surface-hover text-warm-800 font-display text-sm py-2 rounded-full transition-all"
                            data-testid="cover-pass-decline"
                        >
                            not today
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
